import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  RECURRING_CATEGORIES,
  FREQUENCY_OPTIONS,
  type RecurringExpense,
  type RecurringFrequency,
} from '@/lib/recurringCalculations';
import { cn } from '@/lib/utils';

interface RecurringExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense?: RecurringExpense | null;
  onSaved: () => void;
  currency: string;
}

const CURRENCIES = ['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY'];

export const RecurringExpenseDialog = ({
  open,
  onOpenChange,
  expense,
  onSaved,
  currency: defaultCurrency,
}: RecurringExpenseDialogProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const isEditing = !!expense;

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [category, setCategory] = useState('subscription');
  const [frequency, setFrequency] = useState<RecurringFrequency>('monthly');
  const [nextDueDate, setNextDueDate] = useState<Date>(new Date());
  const [isVariableAmount, setIsVariableAmount] = useState(false);
  const [isAutoPaid, setIsAutoPaid] = useState(true);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when dialog opens/closes or expense changes
  useEffect(() => {
    if (open) {
      if (expense) {
        setName(expense.name);
        setAmount(expense.amount.toString());
        setCurrency(expense.currency);
        setCategory(expense.category);
        setFrequency(expense.frequency);
        setNextDueDate(new Date(expense.next_due_date));
        setIsVariableAmount(expense.is_variable_amount);
        setIsAutoPaid(expense.is_auto_paid);
        setNote(expense.note || '');
      } else {
        // Reset to defaults for new expense
        setName('');
        setAmount('');
        setCurrency(defaultCurrency);
        setCategory('subscription');
        setFrequency('monthly');
        setNextDueDate(new Date());
        setIsVariableAmount(false);
        setIsAutoPaid(true);
        setNote('');
      }
    }
  }, [open, expense, defaultCurrency]);

  // Parse amount supporting both comma and dot as decimal separators
  const parseAmount = (value: string): number => {
    if (!value) return 0;
    // Replace comma with dot for parsing
    const normalized = value.replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const parsedAmount = parseAmount(amount);
  const isValidAmount = parsedAmount > 0;

  const handleSave = async () => {
    if (!name.trim() || !isValidAmount) {
      toast({
        title: t('common.invalidInput'),
        description: 'Please enter a name and valid amount',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const expenseData = {
        user_id: user.id,
        name: name.trim(),
        amount: parsedAmount,
        currency,
        category,
        frequency,
        next_due_date: format(nextDueDate, 'yyyy-MM-dd'),
        is_variable_amount: isVariableAmount,
        is_auto_paid: isAutoPaid,
        note: note.trim() || null,
        is_active: true,
      };

      if (isEditing && expense) {
        const { error } = await supabase
          .from('recurring_expenses')
          .update(expenseData)
          .eq('id', expense.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('recurring_expenses')
          .insert(expenseData);
        if (error) throw error;
      }

      toast({
        description: isEditing ? 'Recurring expense updated' : 'Recurring expense added',
      });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error('Save recurring expense error:', error);
      toast({
        title: t('common.error'),
        description: 'Failed to save recurring expense',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader className="text-left pb-4">
          <SheetTitle>
            {isEditing ? 'Edit Recurring Expense' : 'Add Recurring Expense'}
          </SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Update the details of this recurring payment'
              : 'Track subscriptions, bills, and regular payments'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Netflix, Rent, Phone Plan"
              className="min-h-touch"
            />
          </div>

          {/* Amount and Currency */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="min-h-touch"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="currency" className="min-h-touch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((cur) => (
                    <SelectItem key={cur} value={cur}>
                      {cur}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Variable amount toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="variable" className="cursor-pointer">
                Variable amount
              </Label>
              <p className="text-xs text-muted-foreground">
                Amount changes each time (e.g., electricity)
              </p>
            </div>
            <Switch
              id="variable"
              checked={isVariableAmount}
              onCheckedChange={setIsVariableAmount}
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category" className="min-h-touch">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRING_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <span className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label htmlFor="frequency">Frequency</Label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as RecurringFrequency)}>
              <SelectTrigger id="frequency" className="min-h-touch">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((freq) => (
                  <SelectItem key={freq.value} value={freq.value}>
                    {freq.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Next Due Date */}
          <div className="space-y-2">
            <Label>Next payment date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full min-h-touch justify-start text-left font-normal',
                    !nextDueDate && 'text-muted-foreground'
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {nextDueDate ? format(nextDueDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={nextDueDate}
                  onSelect={(date) => date && setNextDueDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Auto-paid toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="autopaid" className="cursor-pointer">
                Auto-paid
              </Label>
              <p className="text-xs text-muted-foreground">
                Automatically deducted (direct debit)
              </p>
            </div>
            <Switch
              id="autopaid"
              checked={isAutoPaid}
              onCheckedChange={setIsAutoPaid}
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Note (optional)</Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Family plan, shared with roommate"
              className="min-h-touch"
            />
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !isValidAmount}
            className="w-full min-h-touch"
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Recurring Expense'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
